import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { adminCases } from "@/lib/admin-endpoints";
import { StatusPill, EligibilityPill } from "@/components/AdminLayout";

const PAGE_SIZE = 50;

export function AdminCasesPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [eligFilter, setEligFilter] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "cases", statusFilter, eligFilter, offset],
    queryFn: () =>
      adminCases.list({
        status: statusFilter || undefined,
        eligibility: eligFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  function resetPage() {
    setOffset(0);
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Cases</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {isLoading ? "Loading…" : `${total.toLocaleString()} total`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); resetPage(); }}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="in_progress">In progress</option>
          <option value="submitted">Submitted</option>
          <option value="closed">Closed</option>
        </select>

        <select
          value={eligFilter}
          onChange={(e) => { setEligFilter(e.target.value); resetPage(); }}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
        >
          <option value="">All eligibility</option>
          <option value="eligible">Eligible</option>
          <option value="manual_review">Manual review</option>
          <option value="out_of_scope">Out of scope</option>
        </select>

        {(statusFilter || eligFilter) && (
          <button
            onClick={() => { setStatusFilter(""); setEligFilter(""); resetPage(); }}
            className="text-sm text-slate-500 hover:text-slate-900 px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors"
          >
            Clear filters ×
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        {isError ? (
          <div className="p-8 text-center text-sm text-red-500">
            Failed to load cases. Check your connection or re-login.
          </div>
        ) : isLoading ? (
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse bg-slate-50/60 mx-4 my-2 rounded-xl" />
            ))}
          </div>
        ) : !data?.items.length ? (
          <div className="p-12 text-center text-sm text-slate-400">
            No cases match the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Case ID</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-3 py-3">Owner</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-3 py-3 hidden sm:table-cell">Status</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-3 py-3 hidden md:table-cell">Eligibility</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-3 py-3 hidden lg:table-cell">Paid</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-3 py-3 hidden lg:table-cell">Created</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-xs text-slate-500">{c.id.slice(0, 8)}…</span>
                    <span className="ml-2 text-xs font-medium text-slate-700">{c.case_type}</span>
                  </td>
                  <td className="px-3 py-3.5">
                    <div className="text-xs font-medium text-slate-800 truncate max-w-[160px]">
                      {c.owner_name ?? c.owner_email}
                    </div>
                    {c.owner_name && (
                      <div className="text-[10px] text-slate-400 truncate max-w-[160px]">{c.owner_email}</div>
                    )}
                  </td>
                  <td className="px-3 py-3.5 hidden sm:table-cell">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="px-3 py-3.5 hidden md:table-cell">
                    <EligibilityPill status={c.eligibility_status} />
                  </td>
                  <td className="px-3 py-3.5 hidden lg:table-cell">
                    {c.paid_at ? (
                      <span className="text-xs font-medium text-emerald-600">✓ Paid</span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3.5 hidden lg:table-cell">
                    <span className="text-xs text-slate-400">
                      {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </td>
                  <td className="px-3 py-3.5 text-right">
                    <Link
                      to={`/admin/cases/${c.id}`}
                      className="text-xs font-semibold text-violet-600 hover:text-violet-700 px-3 py-1.5 rounded-lg hover:bg-violet-50 transition-colors"
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-slate-100 px-5 py-3.5 flex items-center justify-between">
            <span className="text-xs text-slate-400">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Prev
              </button>
              <button
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
